import React, { useState, useEffect } from 'react';
import {
  View,
  Heading,
  Divider,
  TextField,
  DatePicker,
  Button,
  Flex,
  Text,
  Well,
  Content,
  ProgressCircle
} from '@adobe/react-spectrum';
import ArrowLeft from '@spectrum-icons/workflow/ArrowLeft';
import CheckmarkCircle from '@spectrum-icons/workflow/CheckmarkCircle';
import Alert from '@spectrum-icons/workflow/Alert';
import { parseDate } from '@internationalized/date';
import { usePrescriptions } from '../hooks/usePrescriptions';
import { validateEmail, validateName } from '../utils/validation';

export function PrescriptionEditForm({ prescription, customerData, onSuccess, onCancel, ims }) {
  const { update } = usePrescriptions(customerData, ims);
  const [formData, setFormData] = useState({
    customerEmail: '',
    customerName: '',
    orderNumber: '',
    dob: '',
    fileUrl: '' 
  });

  const [dateValue, setDateValue] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [emailError, setEmailError] = useState(null);
  const [nameError, setNameError] = useState(null);

  useEffect(() => {
    if (prescription) {
      setFormData({
        customerEmail: prescription.email || customerData?.email || '',
        customerName: prescription.name || '',
        orderNumber: prescription.order_number || '',
        dob: prescription.dob || '',
        fileUrl: prescription.file_url || ''
      });

      if (prescription.dob) {
        try {
          setDateValue(parseDate(prescription.dob));
        } catch (e) {
          console.error('Invalid date:', e);
        }
      }
    }
  }, [prescription, customerData]);

  const handleSubmit = async (e) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }

    const emailErr = validateEmail(formData.customerEmail);
    setEmailError(emailErr);
    const nameErr = validateName(formData.customerName);
    setNameError(nameErr);
    if (emailErr || nameErr) {
      return;
    }

    setUpdating(true);
    setError(null);

    try {
      await update.mutateAsync({
        id: prescription.id,
        customerEmail: formData.customerEmail,
        customerName: formData.customerName,
        orderNumber: formData.orderNumber,
        dob: formData.dob,
        fileUrl: formData.fileUrl,
        // Editing doesn't change the file — preserve the stored hash.
        fileHash: prescription.params?.hash ?? prescription.hash ?? prescription.file_hash,
        // Preserve the original creation timestamp; the action stamps the edit time.
        createdTimestamp: prescription.params?.timestamp ?? prescription.timestamp,
        ims: ims
      });

      setSuccess(true);
      
      setTimeout(() => {
        if (onSuccess) onSuccess();
        if (onCancel) onCancel();
      }, 1500);

    } catch (err) {
      setError(err.message || 'Failed to update prescription');
    } finally {
      setUpdating(false);
    }
  };

  return (
    <View padding="size-200" width="100%">
      <Flex 
        direction="column" 
        gap="size-300"
        alignItems="center"
      >
        <View width="100%" maxWidth="size-6000" marginTop="size-600">
          <Flex direction="column">
             <Heading level={2}>Edit Prescription</Heading>
              <Divider size="S"/>
            <Content marginTop="size-200">
            <form onSubmit={handleSubmit}>
              <Flex direction="column" gap="size-200">
                <TextField
                  label="Email"
                  width="100%"
                  value={formData.customerEmail}
                  onChange={(value) => { setFormData({ ...formData, customerEmail: value }); setEmailError(null); }}
                  onBlur={() => setEmailError(validateEmail(formData.customerEmail))}
                  isDisabled={updating}
                  validationState={emailError ? 'invalid' : undefined}
                  errorMessage={emailError}
                />
                <TextField
                  label="Name"
                  width="100%"
                  value={formData.customerName}
                  onChange={(value) => { setFormData({ ...formData, customerName: value }); setNameError(null); }}
                  onBlur={() => setNameError(validateName(formData.customerName))}
                  isDisabled={updating}
                  validationState={nameError ? 'invalid' : undefined}
                  errorMessage={nameError}
                />
                <TextField
                  label="Order Number"
                  width="100%"
                  value={formData.orderNumber}
                  onChange={(value) => setFormData({ ...formData, orderNumber: value })}
                  isDisabled={updating}
                />

                <Flex direction="column" gap="size-100" width="100%">
                <Text UNSAFE_style={{ fontSize: '12px', color: '#3e3c3cff' }}>Date of Birth</Text>
                <input
                  id="dob"
                  type="date"
                  value={formData.dob}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFormData(prev => ({ ...prev, dob: value }));
                  }}
                  max={new Date().toISOString().split('T')[0]} 
                  disabled={updating} 
                  required
                  style={{ height: '30px' }}
                />
                </Flex>

                {updating && (
                  <Well>
                    <Flex direction="row" gap="size-200" alignItems="center">
                      <ProgressCircle aria-label="Updating" isIndeterminate size="S" />
                      <Text>Updating prescription...</Text>
                    </Flex>
                  </Well>
                )}
                {success && (
                  <Well>
                    <Flex direction="row" gap="size-200" alignItems="center">
                      <CheckmarkCircle color="positive" size="S" />
                      <Text>
                        <strong>Success!</strong> Prescription updated successfully.
                      </Text>
                    </Flex>
                  </Well>
                )}
                {error && (
                  <Well>
                    <Flex direction="row" gap="size-200" alignItems="center">
                      <Alert color="negative" size="S" />
                      <Text>
                        <strong>Error:</strong> {error}
                      </Text>
                    </Flex>
                  </Well>
                )}
                <Flex direction="row" gap="size-200" marginTop="size-200" justifyContent="end">
                  <Button 
                    variant="secondary" 
                    onPress={onCancel} 
                    isDisabled={updating}
                  >
                    Cancel
                  </Button>
                  <Button 
                    variant="cta" 
                    onPress={handleSubmit} 
                    isDisabled={updating}
                  >
                    {updating ? 'Saving...' : 'Save Changes'}
                  </Button>
                </Flex>
              </Flex>
            </form>
            </Content>
          </Flex>
        </View>
      </Flex>
    </View>
  );
}